import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { useFetch, PageHeader, Loading, ConfirmDialog } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RotateCcw, Trash2, AlertTriangle } from "lucide-react";

export default function Trash() {
  const { user } = useApp();
  const { data, loading, refetch } = useFetch("/trash", []);
  const [del, setDel] = useState(null);
  const [empty, setEmpty] = useState(false);
  const isSuper = user?.role === "super_admin";

  const restore = async (row) => {
    try { await client.post("/trash/restore", { collection: row.collection, id: row.id }); toast.success("Restored"); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const permaDelete = async () => {
    try { await client.post("/trash/delete", { collection: del.collection, id: del.id }); toast.success("Permanently deleted"); setDel(null); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const emptyTrash = async () => {
    try { const r = await client.post("/trash/empty"); toast.success(`Deleted ${r.data.deleted} records`); setEmpty(false); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Trash / Recycle Bin" subtitle="Restore archived records or permanently delete them" testid="trash-page">
        {isSuper && (data || []).length > 0 && <Button variant="outline" className="text-rose-600" onClick={() => setEmpty(true)} data-testid="empty-trash"><Trash2 size={16} className="me-1" /> Empty Trash</Button>}
      </PageHeader>
      {loading ? <Loading /> : (data || []).length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">Trash is empty 🎉</Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border"><tr>
              {["Type", "Record", "Deleted At", "By", ""].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {(data || []).map((r) => (
                <tr key={`${r.collection}-${r.id}`} className="hover:bg-muted/40" data-testid={`trash-${r.id}`}>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary">{r.kind}</span></td>
                  <td className="px-4 py-3 font-medium">{r.label}</td>
                  <td className="px-4 py-3 text-muted-foreground">{(r.deleted_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.deleted_by || "—"}</td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => restore(r)} data-testid={`restore-${r.id}`}><RotateCcw size={15} className="me-1" /> Restore</Button>
                    {isSuper && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDel(r)} data-testid={`perma-${r.id}`}><Trash2 size={15} /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Permanently delete?" description={`This cannot be undone. Permanently delete "${del?.label}"?`} confirmText="Delete Forever" onConfirm={permaDelete} />
      <ConfirmDialog open={empty} onOpenChange={setEmpty} title="Empty entire trash?" description="This will permanently delete ALL archived records. This cannot be undone." confirmText="Empty Trash" onConfirm={emptyTrash} />
    </div>
  );
}
